import React, { useState, useEffect, useCallback } from "react";
import {
    Box, Typography, Button, Paper, Table, TableBody, TableCell,
    TableContainer, TableHead, TableRow, Avatar, Stack, Chip,
    CircularProgress, TextField, MenuItem, Card, Grid
} from "@mui/material";
import {
    Restaurant as TotalIcon, People as StaffIcon, PersonAdd as GuestIcon,
    School as StudentIcon, Update as UpdateIcon
} from "@mui/icons-material";
import { db, auth } from "../../firebase";
import {
    collection, getDocs, getDoc, query, where, doc, setDoc, Timestamp, orderBy
} from "firebase/firestore";
import Swal from "sweetalert2";

const PRIMARY = "#0d9488";

function CanteenEntry() {
    const [loading, setLoading] = useState(false);
    const [attendanceRecords, setAttendanceRecords] = useState({});
    const [manualStaffCount, setManualStaffCount] = useState(0);
    const [manualGuestCount, setManualGuestCount] = useState(0);
    const [timings, setTimings] = useState([]);
    const [selectedTiming, setSelectedTiming] = useState("");
    const [selectedMeal, setSelectedMeal] = useState("Breakfast");
    const [date, setDate] = useState(new Date().toISOString().split('T')[0]);
    const [schoolId, setSchoolId] = useState(null);

    const mealTypes = ["Breakfast", "Lunch", "Snacks", "Dinner"];

    // Filter: Only show students whose status is NOT "Absent"
    const eligibleStudents = Object.values(attendanceRecords)
        .filter(student => student.status !== "Absent")
        .sort((a, b) => a.name.localeCompare(b.name));

    const studentCount = eligibleStudents.length;
    const totalPlates = studentCount + Number(manualStaffCount) + Number(manualGuestCount);

    useEffect(() => {
        const init = async () => {
            const userSnap = await getDoc(doc(db, "users", auth.currentUser.uid));
            if (userSnap.exists()) {
                const sId = userSnap.data().schoolId;
                setSchoolId(sId);
                fetchTimings();
            }
        };
        init();
    }, []);

    const fetchTimings = async () => {
        try {
            const q = query(collection(db, "hostel_attendance_timings"), orderBy("createdAt", "asc"));
            const snap = await getDocs(q);
            const timeList = snap.docs.map(d => ({ id: d.id, ...d.data() }));
            setTimings(timeList);
            if (timeList.length > 0) setSelectedTiming(timeList[0].id);
        } catch (err) { console.error("Timings Error:", err); }
    };

    const fetchData = useCallback(async () => {
        if (!schoolId || !selectedTiming) return;
        setLoading(true);
        try {
            const docId = `${date}_${selectedMeal}_${schoolId}`;

            // 1. Check Canteen Collection first (Saved Canteen Data)
            const canteenSnap = await getDoc(doc(db, "canteen_attendance", docId));

            if (canteenSnap.exists()) {
                const data = canteenSnap.data();
                setAttendanceRecords(data.records || {});
                setManualStaffCount(data.staffCount || 0);
                setManualGuestCount(data.guestCount || 0);
            } else {
                // 2. Fallback: Check Hostel Attendance (Source of Truth for Attendance)
                // Using selectedTiming or selectedMeal depending on how you index hostel records
                const hostelDocId = `${date}_${selectedTiming}_${schoolId}`;
                const hostelSnap = await getDoc(doc(db, "hostel_attendance", hostelDocId));

                if (hostelSnap.exists()) {
                    const hData = hostelSnap.data();
                    setAttendanceRecords(hData.records || {});
                    setManualStaffCount(0); // Reset manual counts for new meal entries
                    setManualGuestCount(0);
                } else {
                    // 3. Last Resort: Fetch all live students from Master List
                    const qAlloc = query(collection(db, "roomallocations"),
                        where("schoolId", "==", schoolId),
                        where("status", "==", "Live")
                    );
                    const allocSnap = await getDocs(qAlloc);
                    const initialMap = {};
                    allocSnap.docs.forEach(d => {
                        const s = d.data();
                        initialMap[s.studentId] = {
                            studentId: s.studentId,
                            name: s.studentName,
                            roomNo: s.roomId,
                            status: "Present"
                        };
                    });
                    setAttendanceRecords(initialMap);
                }
            }
        } catch (err) { console.error("Fetch Error:", err); }
        setLoading(false);
    }, [date, schoolId, selectedTiming, selectedMeal]);

    useEffect(() => { fetchData(); }, [fetchData]);

    const saveCanteenData = async () => {
        setLoading(true);
        try {
            const docId = `${date}_${selectedMeal}_${schoolId}`;
            const timeLabel = timings.find(t => t.id === selectedTiming)?.title || "";

            // Save to CANTEEN specific collection
            await setDoc(doc(db, "canteen_attendance", docId), {
                date,
                schoolId,
                mealType: selectedMeal,
                timingId: selectedTiming,
                timingTitle: timeLabel,
                records: attendanceRecords,
                studentCount,
                staffCount: Number(manualStaffCount),
                guestCount: Number(manualGuestCount),
                totalHeadCount: totalPlates,
                updatedAt: Timestamp.now(),
                markedBy: auth.currentUser.uid
            });

            Swal.fire("Sync Successful", `Plates for ${selectedMeal} recorded.`, "success");
        } catch (err) { Swal.fire("Error", err.message, "error"); }
        setLoading(false);
    };

    return (
        <Box p={1} sx={{ bgcolor: "#f8fafc", minHeight: "100vh" }}>
            <Paper sx={{ p: 2, mb: 3, borderRadius: 2 }} elevation={0}>
                <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} justifyContent="space-between" alignItems="center">
                    <Typography variant="h6" fontWeight="bold">Canteen Plate Entry</Typography>
                    <Stack direction="row" spacing={2}>
                        <TextField select size="small" label="Meal Type" sx={{ width: 140 }} value={selectedMeal} onChange={(e) => setSelectedMeal(e.target.value)}>
                            {mealTypes.map(m => <MenuItem key={m} value={m}>{m}</MenuItem>)}
                        </TextField>
                        <TextField select size="small" label="Time Slot" sx={{ width: 140 }} value={selectedTiming} onChange={(e) => setSelectedTiming(e.target.value)}>
                            {timings.map(t => <MenuItem key={t.id} value={t.id}>{t.title}</MenuItem>)}
                        </TextField>
                        <TextField type="date" size="small" value={date} onChange={(e) => setDate(e.target.value)} InputLabelProps={{ shrink: true }} />
                    </Stack>
                </Stack>
            </Paper>

            <Grid className="row" spacing={2} mb={3}>
                <Grid className="col-sm-3 mb-1" item xs={12} sm={6} md={3}>
                    <Card sx={{ p: 2, textAlign: 'center', bgcolor: PRIMARY, color: '#fff' }}>
                        <TotalIcon />
                        <Typography variant="h4" fontWeight="900">{totalPlates}</Typography>
                        <Typography variant="caption">Total Billing Plates</Typography>
                    </Card>
                </Grid>
                <Grid className="col-sm-3 mb-1" item xs={12} sm={6} md={3}>
                    <Card sx={{ p: 2, textAlign: 'center' }}>
                        <StudentIcon color="primary" />
                        <Typography variant="h4" fontWeight="bold">{studentCount}</Typography>
                        <Typography variant="caption">Students Present</Typography>
                    </Card>
                </Grid>
                <Grid className="col-sm-3 mb-1" item xs={12} sm={6} md={3}>
                    <Card sx={{ p: 2, textAlign: 'center' }}>
                        <StaffIcon color="secondary" />
                        <Typography>
                            <TextField
                                type="number" variant="standard" size="small"
                                value={manualStaffCount} onChange={(e) => setManualStaffCount(e.target.value)}
                                inputProps={{ style: {fontSize:25, textAlign: 'center', fontWeight: 'bold' } }}
                            />
                        </Typography>
                        <Typography variant="caption">Staff Plates</Typography>

                    </Card>
                </Grid>
                <Grid className="col-sm-3 mb-1" item xs={12} sm={6} md={3}>
                    <Card sx={{ p: 2, textAlign: 'center' }}>
                        <GuestIcon color="warning" />
                        <Typography>
                            <TextField
                                type="number" variant="standard" size="small"
                                value={manualGuestCount} onChange={(e) => setManualGuestCount(e.target.value)}
                                inputProps={{ style: {fontSize:25, textAlign: 'center', fontWeight: 'bold' } }}
                            />
                        </Typography>
                        <Typography variant="caption">Guest Plates</Typography>

                    </Card>
                </Grid>
            </Grid>

            <TableContainer component={Paper} elevation={0} sx={{ borderRadius: 2, border: "1px solid #e2e8f0" }}>
                {loading ? (
                    <Box p={5} textAlign="center"><CircularProgress sx={{ color: PRIMARY }} /></Box>
                ) : (
                    <Table size="small">
                        <TableHead sx={{ bgcolor: "#f1f5f9" }}>
                            <TableRow>
                                <TableCell>Name</TableCell>
                                <TableCell>Room</TableCell>
                                <TableCell align="right">Hostel Status</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {eligibleStudents.map((s) => (
                                <TableRow key={s.studentId}>
                                    <TableCell>
                                        <Stack direction="row" spacing={1} alignItems="center">
                                            <Avatar sx={{ width: 28, height: 28, bgcolor: PRIMARY, fontSize: 12 }}>{s.name[0]}</Avatar>
                                            <Typography variant="body2">{s.name}</Typography>
                                        </Stack>
                                    </TableCell>
                                    <TableCell><Chip label={s.roomNo} size="small" variant="outlined" /></TableCell>
                                    <TableCell align="right">
                                        <Chip label="COUNTED" size="small" sx={{ bgcolor: '#dcfce7', color: '#166534' }} />
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                )}
            </TableContainer>

            <Box mt={3} textAlign="right">
                <Button
                    variant="contained" size="large" startIcon={<UpdateIcon />}
                    sx={{ bgcolor: PRIMARY, px: 5, borderRadius: 2 }}
                    onClick={saveCanteenData} disabled={loading}
                >
                    Save Canteen Records
                </Button>
            </Box>
        </Box>
    );
}

export default CanteenEntry;